/**
 * ist-zoom.js
 * Global lightbox modal + zoom-button auto-initializer.
 * Shared by: image-slideshow-with-text, multicolumn, collage.
 *
 * Exports on window:
 *   window.istGetModal()  — singleton IstModal
 *   window.istInitZoom()  — re-scan for new .ist-zoom-btn buttons
 *
 * IstModal.open(arg, index):
 *   open(srcString)              → single-image mode (ist-zoom-btn)
 *   open(slideshowInstance, idx) → slideshow mode (IstSlideshow)
 */

(function () {
  'use strict';

  class IstModal {
    constructor() {
      this.el             = this._buildEl();
      this.activeInstance = null;
      this.singleSrc      = null;
      this.index          = 0;
      this._keyHandler    = null;
      document.body.appendChild(this.el);
    }

    open(arg, index) {
      if (typeof arg === 'string') {
        this.activeInstance = null;
        this.singleSrc      = arg;
        this.index          = 0;
      } else {
        this.activeInstance = arg;
        this.singleSrc      = null;
        this.index          = index || 0;
      }
      this._render();
      this.el.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this._keyHandler = (e) => {
        if (e.key === 'Escape')     this.close();
        if (e.key === 'ArrowRight') this._next();
        if (e.key === 'ArrowLeft')  this._prev();
      };
      document.addEventListener('keydown', this._keyHandler);
    }

    close() {
      this.el.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this._keyHandler) {
        document.removeEventListener('keydown', this._keyHandler);
        this._keyHandler = null;
      }
    }

    _next() {
      if (!this.activeInstance) return;
      const total = this.activeInstance.slides.length;
      this.index  = (this.index + 1) % total;
      this._render();
    }

    _prev() {
      if (!this.activeInstance) return;
      const total = this.activeInstance.slides.length;
      this.index  = (this.index - 1 + total) % total;
      this._render();
    }

    _render() {
      const modalImg = this.el.querySelector('.ist-modal__img');
      const counter  = this.el.querySelector('.ist-modal__counter');
      const prevBtn  = this.el.querySelector('.ist-modal__arrow--prev');
      const nextBtn  = this.el.querySelector('.ist-modal__arrow--next');

      if (this.singleSrc) {
        modalImg.src          = this.singleSrc;
        modalImg.alt          = '';
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
        counter.style.display = 'none';
      } else {
        const slides   = this.activeInstance.slides;
        const slide    = slides[this.index];
        const zoomSrc  = slide.dataset.zoomSrc;
        const imgEl    = slide.querySelector('img');
        modalImg.src   = (zoomSrc && zoomSrc.length > 0) ? zoomSrc : (imgEl ? imgEl.src : '');
        modalImg.alt   = imgEl ? (imgEl.alt || '') : '';

        const hasMultiple      = slides.length > 1;
        prevBtn.style.display  = hasMultiple ? '' : 'none';
        nextBtn.style.display  = hasMultiple ? '' : 'none';
        counter.style.display  = hasMultiple ? '' : 'none';
        counter.textContent    = `${this.index + 1} / ${slides.length}`;
      }
    }

    _buildEl() {
      const arrowSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>`;
      const el = document.createElement('div');
      el.className = 'ist-modal';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Image viewer');
      el.innerHTML = `
        <div class="ist-modal__backdrop"></div>
        <div class="ist-modal__container">
          <button class="ist-modal__close" type="button" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">
              <path d="M1 1l16 16M17 1L1 17"/>
            </svg>
          </button>
          <button class="ist-modal__arrow ist-modal__arrow--prev" type="button" aria-label="Previous image">${arrowSvg}</button>
          <div class="ist-modal__img-wrap">
            <img class="ist-modal__img" src="" alt="">
          </div>
          <button class="ist-modal__arrow ist-modal__arrow--next" type="button" aria-label="Next image">${arrowSvg}</button>
          <div class="ist-modal__counter"></div>
        </div>
      `;
      el.querySelector('.ist-modal__backdrop').addEventListener('click', () => this.close());
      el.querySelector('.ist-modal__close').addEventListener('click',    () => this.close());
      el.querySelector('.ist-modal__arrow--prev').addEventListener('click', () => this._prev());
      el.querySelector('.ist-modal__arrow--next').addEventListener('click', () => this._next());
      return el;
    }
  }

  let _modal = null;
  function getIstModal() {
    if (!_modal) _modal = new IstModal();
    return _modal;
  }

  function initZoom() {
    document.querySelectorAll('.ist-zoom-btn:not([data-ist-bound])').forEach((btn) => {
      btn.dataset.istBound = '1';
      btn.addEventListener('click', () => {
        const src = btn.dataset.istZoomSrc;
        if (src) getIstModal().open(src);
      });
    });
  }

  window.istGetModal = getIstModal;
  window.istInitZoom = initZoom;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initZoom);
  } else {
    initZoom();
  }
  document.addEventListener('shopify:section:load', initZoom);

})();
